import { useState, useCallback, useRef } from 'react';
import { Booking, Resource } from '@/types';

export interface DraggedBooking {
  booking: Booking;
  originalResource: Resource;
  originalStartTime: Date;
  originalEndTime: Date;
}

export interface DragTarget {
  resourceId: string;
  startTime: Date;
  endTime: Date;
}

export interface DragState {
  isDragging: boolean;
  draggedBooking: DraggedBooking | null;
  dragTarget: DragTarget | null;
  dragType: 'move' | 'resize-start' | 'resize-end' | null;
}

export function useBookingDrag() {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedBooking: null,
    dragTarget: null,
    dragType: null,
  });

  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const hasMoved = useRef(false);

  const startDrag = useCallback((
    booking: Booking,
    resource: Resource,
    dragType: 'move' | 'resize-start' | 'resize-end',
    clientX: number,
    clientY: number
  ) => {
    console.log('useBookingDrag: startDrag called', { bookingId: booking.id, dragType, clientX, clientY });
    
    dragStartPosRef.current = { x: clientX, y: clientY };
    hasMoved.current = false;

    setDragState({
      isDragging: true,
      draggedBooking: {
        booking,
        originalResource: resource,
        originalStartTime: new Date(booking.startTime),
        originalEndTime: new Date(booking.endTime),
      },
      dragTarget: null,
      dragType,
    });
    
    console.log('useBookingDrag: dragState updated to isDragging=true');
  }, []);

  const updateDragTarget = useCallback((target: DragTarget | null) => {
    if (dragStartPosRef.current) {
      hasMoved.current = true;
    }
    
    setDragState((prev) => ({
      ...prev,
      dragTarget: target,
    }));
  }, []);

  const endDrag = useCallback(() => {
    const result = {
      draggedBooking: dragState.draggedBooking,
      dragTarget: dragState.dragTarget,
      dragType: dragState.dragType,
      hasMoved: hasMoved.current,
    };

    setDragState({
      isDragging: false,
      draggedBooking: null,
      dragTarget: null,
      dragType: null,
    });

    dragStartPosRef.current = null;
    hasMoved.current = false;

    return result;
  }, [dragState]);

  const cancelDrag = useCallback(() => {
    setDragState({
      isDragging: false,
      draggedBooking: null,
      dragTarget: null,
      dragType: null,
    });

    dragStartPosRef.current = null;
    hasMoved.current = false;
  }, []);

  return {
    dragState,
    startDrag,
    updateDragTarget,
    endDrag,
    cancelDrag,
  };
}
